import { useMemo } from 'react'

import { MESSAGE_STATUS, isUserMessage, isAssistantMessage } from 'components/agent_hub_components'
import { useThreadQuery } from 'features/agent_hub/graphql_api'
import { isValidUUID } from 'shared/utils/uuid'

/**
 * Hook to fetch and format thread messages for the chat interface
 */
export const useThreadMessages = (threadId) => {
  const shouldSkip = !threadId || !isValidUUID(threadId)
  const { data: threadData, isLoading, error } = useThreadQuery(
    {
      input: {
        threadId,
        pagination: {
          last: 50 // Fetch last 50 messages
        }
      }
    },
    {
      skip: shouldSkip
    }
  )

  // Convert thread messages to the format expected by the chat interface
  const messages = useMemo(() => {
    if (!threadData?.thread?.messages?.edges) {
      return []
    }

    // First, convert all messages to the expected format
    const allMessages = threadData.thread.messages.edges.map((edge) => {
      const { node } = edge

      return {
        id: node.messageId,
        role: node.role,
        content: node.content,
        status: MESSAGE_STATUS.SUCCESS,
        queryId: node.messageId, // messageId is the queryId for user messages
        responseId: null,
        timestamp: node.timestamp,
        attachments: node.attachments || null,
      }
    })

    // Sort messages by timestamp ascending (oldest first, newest last)
    const sortedMessages = allMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

    // Group user messages with their corresponding assistant responses
    const groupedMessages = []
    const processedMessageIds = new Set()

    for (const message of sortedMessages) {
      if (!processedMessageIds.has(message.id)) {
        // If it's a user message, look for its corresponding assistant response
        if (isUserMessage(message)) {
          const assistantResponse = sortedMessages.find((msg) => isAssistantMessage(msg)
          && msg.id === `${message.id}-response`
          && !processedMessageIds.has(msg.id)
          )

          // Add user message first
          groupedMessages.push(message)
          processedMessageIds.add(message.id)

          // Add assistant response if it exists
          if (assistantResponse) {
            groupedMessages.push(assistantResponse)
            processedMessageIds.add(assistantResponse.id)
          }
        } else if (isAssistantMessage(message) && !message.id.includes('-response')) {
          // Handle standalone assistant messages (shouldn't happen in normal flow)
          groupedMessages.push(message)
          processedMessageIds.add(message.id)
        }
      }
    }

    return groupedMessages
  }, [threadData])

  return {
    messages,
    isLoading,
    error
  }
}
